import type { FC } from "react"
import { useCallback } from "react"
import { IconLayer, useMapGLContext } from "@wayz/react-gl"
import { getBusinessDistrict } from "@/api"
import type { RouterIdsVO, BusinessDistrictVO } from "@/api"
import { useCreation, useRequest } from "ahooks-v2"
import { useControllerModel, useSelectionModel } from "@/models"
import { useParams } from "react-router-dom"
import { createSVGIcon, svgToDataURL } from "../../createSvg"
import { getBranchThemeColors } from "@/util/commonUtils"
import { viewOffset, viewZoom } from "@/constants/constant"
// ==============================================
import GeographicRange from "@/components/GeographicRange"
// ==============================================
type BusinessItemVO = BusinessDistrictVO & {
  id: string
  color: string
  longitude: number
  latitude: number
}
interface BusinessCircleProps {
  visible?: boolean
}
const BusinessCircle: FC<BusinessCircleProps> = ({ visible = true }) => {
  const { cityId } = useParams<RouterIdsVO>() as any
  const { setViewState } = useMapGLContext()
  const { theme } = useControllerModel()
  const { setAllState: setSelectionAllState } = useSelectionModel()

  // 获取网点主题颜色
  const colorTheme = useCreation(() => {
    return getBranchThemeColors()
    // eslint-disable-next-line
  }, [theme])

  // 获取商圈
  const { data } = useRequest(() => getBusinessDistrict(cityId?.slice(-6)), {
    ready: !!cityId,
    refreshDeps: [cityId],
    onSuccess: (res) => {
      // 缓存商圈数据
      setSelectionAllState({
        businessCircleList: res || [],
      })
    },
  })

  // 商圈数据
  const businessData: BusinessItemVO[] = useCreation(() => {
    if (data?.length) {
      const result: BusinessItemVO[] = []

      for (const item of data || []) {
        result.push({
          ...item,
          longitude: item.lon,
          latitude: item.lat,
          id: item.placeId,
          color: colorTheme.unlockedColor,
        })
      }

      return result
    } else {
      return []
    }
  }, [data, colorTheme])

  // icon
  const getIcon = useCallback(
    (d: any) => {
      const dpr = window.devicePixelRatio
      // 宽高取整, 避免webgl加载纹理texture失败
      const width = Math.floor(200 * dpr)
      const height = Math.floor(59 * dpr)
      const { placeName, predictNum } = d

      return {
        url: svgToDataURL(
          createSVGIcon(
            placeName,
            `${predictNum} 杯`,
            "",
            colorTheme.unlockedColor,
            colorTheme.fillColor,
            width,
            height,
            "咖啡需求预测",
            "",
            85,
          ),
        ),
        width,
        height,
      }
    },
    [colorTheme],
  )

  // 点击提示框
  const onClick = useCallback(
    ({ object }) => {
      // 地图定位缩放
      setViewState({
        longitude: object.longitude + viewOffset,
        latitude: object.latitude,
        zoom: viewZoom,
      })
    },
    [setViewState],
  )

  return (
    <>
      {/* polygon */}
      <GeographicRange
        id="mall-map-polygon"
        visible={visible}
        data={businessData as any}
        zIndex={2}
      />
      {/* 点 */}
      <IconLayer
        id="business-circle-points"
        visible={visible}
        pickable={true}
        billboard={false}
        getPosition={(d) => [d.longitude, d.latitude]}
        data={businessData}
        iconMapping={{
          location: { x: 0, y: 0, width: 48, height: 62, mask: false },
        }}
        getIcon={getIcon}
        getSize={1}
        getPixelOffset={[0, -24]}
        sizeScale={60}
        zIndex={9}
        onClick={onClick}
        updateTriggers={{ getIcon }}
      />
    </>
  )
}

export default BusinessCircle
